import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rqKeys } from '@/react-query-keys'
import type { T_CertificateRestBackend } from '@/helpers/certificates-rest'

const LEGACY_LS_KEY_CERTIFICATES_REST = 'era_certificates_rest'
const lsKeyForUser = (userId: number) => `era_certificates_rest_u_${userId}`

const safeParseJson = (raw: string | null): unknown => {
  try {
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const normalizeUserId = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : NaN
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

const normalizeFiniteNumber = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : NaN
  if (!Number.isFinite(n)) return null
  return n
}

const normalizeEntry = (raw: any): T_CertificateRestBackend | null => {
  if (!raw || typeof raw !== 'object') return null

  const certificateId = normalizeFiniteNumber(raw.certificateId)
  const nominalId = normalizeFiniteNumber(raw.nominalId)
  const amount = normalizeFiniteNumber(raw.amount)
  const updatedAtTs = normalizeFiniteNumber(raw.updatedAtTs)
  const userId = normalizeUserId(raw?.userData?.id)

  if (certificateId === null || nominalId === null || amount === null || updatedAtTs === null) return null
  if (userId === null) return null
  if (certificateId <= 0 || nominalId <= 0) return null

  return {
    id: Number.isFinite(Number(raw.id)) ? Number(raw.id) : Date.now(),
    certificateId,
    nominalId,
    amount,
    userData: { id: userId },
    updatedAtTs,
  }
}

const normalizeListForUser = (parsed: unknown, userId: number): T_CertificateRestBackend[] => {
  if (!Array.isArray(parsed)) return []

  const candidates: T_CertificateRestBackend[] = []
  for (const item of parsed) {
    const normalized = normalizeEntry(item)
    if (!normalized) continue
    if (normalized.userData?.id !== userId) continue
    candidates.push(normalized)
  }

  // Deduplicate by (certificateId, nominalId): keep newest by updatedAtTs
  const map = new Map<string, T_CertificateRestBackend>()
  for (const item of candidates) {
    const key = `${item.certificateId}-${item.nominalId}`
    const existing = map.get(key)
    if (!existing || item.updatedAtTs >= existing.updatedAtTs) {
      map.set(key, item)
    }
  }

  return Array.from(map.values())
}

const getLocalCertificatesRest = (userId: number): T_CertificateRestBackend[] => {
  const userKey = lsKeyForUser(userId)
  const rawUser = localStorage.getItem(userKey)
  if (rawUser) {
    const parsedUser = safeParseJson(rawUser)
    const normalized = normalizeListForUser(parsedUser, userId)
    const normalizedRaw = JSON.stringify(normalized)
    if (normalizedRaw !== rawUser) {
      try {
        localStorage.setItem(userKey, normalizedRaw)
      } catch {
        // ignore
      }
    }
    return normalized
  }

  const rawLegacy = localStorage.getItem(LEGACY_LS_KEY_CERTIFICATES_REST)
  if (!rawLegacy) {
    // Mark initialized so we don't try legacy key on every read for empty users
    try {
      localStorage.setItem(userKey, '[]')
    } catch {
      // ignore
    }
    return []
  }

  const parsedLegacy = safeParseJson(rawLegacy)
  const migrated = normalizeListForUser(parsedLegacy, userId)

  // Even if empty, persist to avoid repeated legacy scanning
  try {
    localStorage.setItem(userKey, JSON.stringify(migrated))
  } catch {
    // ignore
  }

  return migrated
}

const saveLocalCertificatesRest = (userId: number, list: T_CertificateRestBackend[]) => {
  try {
    const key = lsKeyForUser(userId)
    const nextRaw = JSON.stringify(list)
    const prevRaw = localStorage.getItem(key)
    if (prevRaw !== nextRaw) {
      localStorage.setItem(key, nextRaw)
    }
  } catch {
    // ignore
  }
}

export const useCertificatesRest = (userId?: number) => {
  return useQuery<T_CertificateRestBackend[]>({
    queryKey: [ rqKeys.certificatesRest, userId ],
    queryFn: async () => {
      if (!userId) return []
      return getLocalCertificatesRest(userId)
    },
    enabled: true,
  })
}

export const useUpdateCertificateRest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      certificateId: number
      nominalId: number
      amount: number
      userData: any
      updatedAtTs: number
    }) => {
      const userId = normalizeUserId(data?.userData?.id)
      if (!userId) {
        throw new Error('Cannot update certificate rest: userId is missing')
      }

      const certificateId = normalizeFiniteNumber(data?.certificateId)
      const nominalId = normalizeFiniteNumber(data?.nominalId)
      const amount = normalizeFiniteNumber(data?.amount)
      const updatedAtTs = normalizeFiniteNumber(data?.updatedAtTs)

      if (certificateId === null || certificateId <= 0) throw new Error('Invalid certificateId')
      if (nominalId === null || nominalId <= 0) throw new Error('Invalid nominalId')
      if (amount === null) throw new Error('Invalid amount')
      if (updatedAtTs === null) throw new Error('Invalid updatedAtTs')

      const currentList = getLocalCertificatesRest(userId)

      const existingIndex = currentList.findIndex((item) => (
        item.certificateId === certificateId
        && item.nominalId === nominalId
      ))

      const newItem: T_CertificateRestBackend = {
        id: Date.now(), // Генерируем временный ID
        certificateId,
        nominalId,
        amount,
        userData: { id: userId },
        updatedAtTs,
      }

      if (existingIndex !== -1) {
        currentList[existingIndex] = {
          ...currentList[existingIndex],
          amount,
          updatedAtTs,
          userData: { id: userId },
        }
      } else {
        currentList.push(newItem)
      }

      saveLocalCertificatesRest(userId, currentList)
      return newItem
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ rqKeys.certificatesRest ] })
    },
  })
}
